import { george, pr, theme } from "aaa";
import { ChooseAddressResponsive } from "components/common/ChooseAddress";
import { ChooseEmailResponsive } from "components/common/ChooseEmail";
import { ChoosePhoneResponsive } from "components/common/ChoosePhone";
import { InfoBoxWithBackground } from "components/common/InfoBoxWithBackground";
import { ui } from "components/util/uiHelpers";
import { FormattedHTMLMessage } from "i18n/util";
import { observer } from "mobx-react";
import * as moment from "moment";
import { themeColors } from "muiTheme";
import * as React from "react";
import profileStore from "stores/ProfileStore";
import { pushRoute } from "stores/RouterFunctions";
import trackingStore from "stores/TrackingStore";
import { openAsLink } from "util/helpers";
import externalInterfaceStore from "../../stores/ExternalInterfaceStore";
import { ActionButton } from "../common/georgeComponents";
import { HouseholdInsuranceColumnContent } from "./components/HouseholdInsuranceColumnContent";
import { HouseholdInsuranceTwoColumnContainer } from "./components/HouseholdInsuranceTwoColumnContainer";
import { HouseholdInsuranceRoutes } from "./HouseholdInsuranceRoutes";
import { HouseholdInsuranceStore } from "./HouseholdInsuranceStore";

const intermediaryDsgvoUrl = "https://www.sparkasse.at/dsgvo";
const gisaUrl = "https://www.gisa.gv.at/";

const dataTitleStyle = { fontWeight: 600 };
const dataIconStyle = {
    color: themeColors.grey_400,
    height: 24,
    width: 24,
}

const dataCardBorderStyle = {
    border: `1px solid ${themeColors.grey_300}`,
    borderRadius: 8,
    padding: "20px 12px",
}


const DataCard = ({
    children,
    style
}: {
    children: React.ReactNode
    style?: React.CSSProperties
}) => {
    return (
        <pr.HFlex
            style={{
                alignItems: "center",
                gap: 16,
                ...dataCardBorderStyle,
                ...style
            }}
        >
            {children}
        </pr.HFlex>
    )
}

type HouseholdInsurancePersonalDataProps = {
    store: HouseholdInsuranceStore
};

export const HouseholdInsurancePersonalData = observer(({ store }: HouseholdInsurancePersonalDataProps) => {
    const [submitting, setSubmitting] = React.useState(false);

    if (store.isLoading) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    const isSubmitEnabled = () => {
        return store.contactsStore.selectedPhoneId &&
            store.contactsStore.selectedAddressId &&
            store.contactsStore.selectedEmailAddressId;
    };

    const PageComponent = externalInterfaceStore.isGeorgeApp ? george.PageWithActionButton : React.Fragment;


    const handleSendInterest = async () => {
        try {
            setSubmitting(true);
            await trackingStore.trackEvent({ eventName: "nextPage" });
            await trackingStore.trackEvent({ eventName: "dataHandover_started" });
            const handoverUrl = await store.sendHouseholdInsuranceInterest();

            if (handoverUrl) {
                openAsLink(
                    externalInterfaceStore.isGeorgeApp
                        ? `georgeapp://at/open_external_link?url=${encodeURIComponent(handoverUrl)}`
                        : handoverUrl,
                    "_blank"
                );
                await trackingStore.trackEvent({ eventName: "dataHandover_succeded" });
                pushRoute(HouseholdInsuranceRoutes.SUCCESS);
            } else {
                await trackingStore.trackEvent({ eventName: "dataHandover_failed" });
                pushRoute("/error");
            }
        } catch (error) {
            await trackingStore.trackEvent({ eventName: "dataHandover_failed" });
            pushRoute("/error");
        }
    }

    return (
        <george.Background>
            <PageComponent>
                <george.Card style={{ marginBottom: theme.MARGIN_SMALL }}>
                    <pr.A11yPopupHider>
                        <pr.VFlex style={{ gap: 16, padding: theme.MARGIN }}>
                            <pr.P_24_TITLE>
                                {ui.__("householdInsurance.personalDataPage.title")}
                            </pr.P_24_TITLE>
                            <pr.P_16_CONTENT>
                                <FormattedHTMLMessage id="householdInsurance.personalDataPage.description" />
                            </pr.P_16_CONTENT>
                        </pr.VFlex>
                    </pr.A11yPopupHider>

                    <pr.VFlex
                        style={{
                            gap: 16,
                            padding: theme.MARGIN
                        }}
                    >
                        <pr.A11yPopupHider>
                            <HouseholdInsuranceTwoColumnContainer disablePadding style={{ alignItems: "stretch" }}>
                                <HouseholdInsuranceColumnContent disableMargin style={{ display: "flex" }}>
                                    <DataCard style={{ flex: 1 }}>
                                        <pr.IconUser
                                            style={dataIconStyle}
                                        />
                                        <pr.VFlex>
                                            <pr.P_16_TITLE style={dataTitleStyle}>
                                                {ui.__("common.name")}
                                            </pr.P_16_TITLE>
                                            <pr.P_16_CONTENT >
                                                {profileStore.name}
                                            </pr.P_16_CONTENT>
                                        </pr.VFlex>
                                    </DataCard>
                                </HouseholdInsuranceColumnContent>
                                <HouseholdInsuranceColumnContent disableMargin style={{ display: "flex" }}>
                                    <DataCard style={{ flex: 1 }}>
                                        <pr.IconCalendar
                                            style={dataIconStyle}
                                        />
                                        <pr.VFlex>
                                            <pr.P_16_TITLE style={dataTitleStyle}>
                                                {ui.__("common.dateOfBirth")}
                                            </pr.P_16_TITLE>
                                            <pr.P_16_CONTENT>
                                                {moment(profileStore.profile?.dateOfBirth).format("DD.MM.YYYY")}
                                            </pr.P_16_CONTENT>
                                        </pr.VFlex>
                                    </DataCard>
                                </HouseholdInsuranceColumnContent>
                            </HouseholdInsuranceTwoColumnContainer>
                        </pr.A11yPopupHider>

                        {/* INPUT FIELDS */}
                        <pr.VFlex
                            style={{
                                gap: 8,
                                ...dataCardBorderStyle,
                            }}
                        >
                            <pr.A11yPopupHider>
                                <ChooseAddressResponsive
                                    contactsStore={store.contactsStore}
                                    icon={<pr.IconLiving style={dataIconStyle} />}
                                    onNewAddress={() => pushRoute(HouseholdInsuranceRoutes.NEW_ADDRESS)}
                                />
                            </pr.A11yPopupHider>
                            <ChooseEmailResponsive
                                contactsStore={store.contactsStore}
                                icon={<pr.IconMailXL style={dataIconStyle} />}
                            />
                            <pr.A11yPopupHider>
                                <ChoosePhoneResponsive
                                    contactsStore={store.contactsStore}
                                    icon={<pr.IconPhoneXL style={dataIconStyle} />}
                                    onNewPhone={() => pushRoute(HouseholdInsuranceRoutes.NEW_PHONE)}
                                />
                            </pr.A11yPopupHider>
                        </pr.VFlex>
                    </pr.VFlex>

                    <pr.A11yPopupHider>
                        <div style={{ padding: theme.MARGIN }}>
                            <InfoBoxWithBackground>
                                <pr.P_16_CONTENT>
                                    <FormattedHTMLMessage
                                        id="householdInsurance.personalDataPage.infoBox"
                                        values={{
                                            dsgvoHref: externalInterfaceStore.isGeorgeApp
                                                ? `georgeapp://at/open_external_link?url=${encodeURIComponent(intermediaryDsgvoUrl)}`
                                                : intermediaryDsgvoUrl,
                                            dsgvoTarget: "_blank"
                                        }}
                                    />
                                </pr.P_16_CONTENT>
                            </InfoBoxWithBackground>
                        </div>
                        <pr.LightSeparatorLine spacing={theme.MARGIN} />
                        <pr.VFlex style={{ gap: 12, padding: theme.MARGIN }}>
                            <pr.P_18_TITLE>
                                {ui.__("householdInsurance.personalDataPage.distributorInformation.title")}
                            </pr.P_18_TITLE>
                            <pr.P_16_CONTENT>
                                <FormattedHTMLMessage id="householdInsurance.personalDataPage.distributorInformation.description"
                                    values={{
                                        gisaCode: profileStore.profile?.gisaCode ? profileStore.profile?.gisaCode : "",
                                        href: externalInterfaceStore.isGeorgeApp ?
                                            `georgeapp://at/open_external_link?url=${encodeURIComponent(gisaUrl)}` :
                                            gisaUrl,
                                        target: "_blank",
                                        name: profileStore.profile?.instituteInfo.longName,
                                        address: profileStore.profile?.instituteInfo.address
                                    }}
                                />
                            </pr.P_16_CONTENT>
                        </pr.VFlex>
                        <pr.P_16_CONTENT
                            style={{
                                padding: theme.MARGIN,
                            }}
                        >
                            <FormattedHTMLMessage
                                id="householdInsurance.personalDataPage.note"
                            />
                        </pr.P_16_CONTENT>
                        <ActionButton
                            disableButton={!isSubmitEnabled() || submitting}
                            primary
                            messageId="householdInsurance.personalDataPage.submit"
                            onClick={handleSendInterest}
                            {...george.getActionButtonWebVersionProps()}
                        />
                    </pr.A11yPopupHider>
                </george.Card>
            </PageComponent >
        </george.Background >
    );
});



// WEBPACK FOOTER //
// ./src/components/householdInsurance/HouseholdInsurancePersonalData.tsx